const { onlyUnique } = require('common/helpers/only-unique')
const PreReqValidator = require('./pre-req-validator')

/**
 * A helper function that helps create input for the course-plan-builder
 */
class InputBuilder {
   /**
    * Helper function to generate input object for course-plan-builder
    * @param {Array} defaultTeachingScheduled the default teaching schedule for course plan
    * @param {Array} teachingScheduled a specific year by year teaching schedule, when not provided, default teaching schedule will be utilised
    * @param {Number} defaultWorkload the default workload for a teaching period when none is provided in the workloads array
    * @param {Array} workloads year by year breakdown of workload
    * @param {Array} lockedUnits collection of units that are locked to a specific year and teaching period
    * @param {Array} completedUnits collection of completed unit codes
    * @param {Array} units collection of units that needs to be scheduled
    * @param {Number} startYear the start year of the course
    * @param {Array} validLocations an array of valid locations for unit offerings
    * @param {Object} teachingPeriods a key value pair of year and teaching periods
    * @returns input object to be used to generate course-plan-builder
    */
   static createInput(defaultTeachingScheduled, teachingScheduled, defaultWorkload, workloads, lockedUnits, completedUnits, units, startYear, validLocations, teachingPeriods, allUnitCodes) {
      startYear = parseInt(startYear);
      const convertedUnits = this.convertUnits(units, startYear, validLocations, teachingPeriods, defaultTeachingScheduled)

      // const unitsWithoutOfferings = convertedUnits.filter(u => u.defaultOfferings.length == 0 && u.offerings.length == 0)
      const completedConvertedUnits = completedUnits && completedUnits.length ? convertedUnits.filter(u => completedUnits.includes(u.code)) : []
      // const missingPreReqUnits = convertedUnits.filter(u => !PreReqValidator.prerequisitesExistTest(u, allUnitCodes, u.prerequisites))
      const localLockedUnits = lockedUnits = lockedUnits.map(x => ({code: x.code, year: parseInt(x.year), teachingPeriod: x.teachingPeriod}))

      // do I need to add missingCoReqs, and missing antiCoReqs here?

      const nonScheduledUnits = [...completedConvertedUnits]

      //remove unscheduled units from the converted units array.
      const scheduledUnits = convertedUnits.filter(u => !nonScheduledUnits.map(u=>u.id).includes(u.id))

      var input = {
         defaultWorkload,
         generations: 100,
         defaultTeachingScheduled,
         teachingScheduled,
         workloads,
         lockedUnits: localLockedUnits,
         nonScheduledUnits,
         units: scheduledUnits,
      }

      return input
   }



   /**
    * Helper functions that convert selected units into a simplified unit object required by course-plan-builder
    * @param {Array} items selected curriculum items
    * @param {Number} startYear the start year of the course
    * @param {Array} validLocations an array of valid locations for unit offerings
    * @param {Object} teachingPeriods a key value pair of year and teaching periods
    * @returns the converted units
    */
   static convertUnits(items, startYear, validLocations, teachingPeriods, defaultTeachingScheduled) {
      if (!validLocations) {
         validLocations = []
      }
      const units = []

      // filter non units and remove duplicate units
      items
         .filter(item => item.relationship.type === "Unit")
         .forEach(unit => {
            if (!units.find(u => u.id === unit.id)) {
               units.push(unit)
            }
         });

      var convertedUnits = units.map(item => {
         return InputBuilder.convertUnit(item, startYear, validLocations, teachingPeriods, defaultTeachingScheduled)
      })

      return convertedUnits;
   }

   static convertUnit(item, startYear, validLocations, teachingPeriods, defaultTeachingScheduled)
   {
      var u = {}
         u.id = item.id;
         u.code = item.relationship.code
         u.order = 4
         if (item.firstLevelTitle?.match(/core/i))
            u.order = 1
         if (item.firstLevelTitle?.match(/major/i))
            u.order = 2
         if (item.firstLevelTitle?.match(/spine/i))
            u.order = 3
         u.firstLevelTitle = item.firstLevelTitle
         u.name = item.name
         u.level = parseInt(item.relationship.code.charAt(item.relationship.code.indexOf(item.relationship.code.match(/\d/)))) || 0
         u.status = item.relationship.status
         u.courseLoopVersionNumber = item.relationship.courseLoopVersionNumber
         u.creditPoints = parseInt(item.relationship.credits) || 0
         if (item.relationship.unit && item.relationship.unit.offerings) {

            // first filter offerings based on their location
            const locationSpecificOfferings = item.relationship.unit.offerings
               .filter(offering => offering.location && offering.location.id && validLocations.includes(offering.location.id))

            // determine the maximum year that this unit is offered on
            // this is used for default upper limit for offerings that has no end period
            const offeringsMaxYear = Math.max.apply(Math, locationSpecificOfferings
               .filter(o => o.startYear)
               .map(o => o.startYear)
            );

            // based start year and end year, create a verbose mapping for each year the unit is offered on
            const specificOfferings = locationSpecificOfferings
               .map(offering => {
                  const mappedOfferings = [];
                  const startEnum = Math.max(offering.startYear, startYear);
                  const endEnum = offering.endYear || offeringsMaxYear;
                  for (let i = startEnum; i <= endEnum; i++) {
                     mappedOfferings.push(
                        {
                           year: (i - startYear) + 1, // convert year to a 1 base index based on start year
                           teachingPeriods: this.mapStandardTeachingPeriods(teachingPeriods, defaultTeachingScheduled, i, offering.teachingScheduled)
                        }
                     )
                  }
                  return mappedOfferings
               });

            // convert offerings to form that can be used by course plan builder
            u.offerings = specificOfferings.reduce((mappedArray, offerings) => {
               offerings.forEach(offering => {
                  //ignore null teaching periods (no mapping available)
                  if (offering.teachingPeriods)
                  {
                     const existingYearIndex = mappedArray.findIndex(arr => arr.year === offering.year);
                     if (existingYearIndex >= 0) {
                        mappedArray[existingYearIndex].teachingPeriods.push(offering.teachingPeriods)
                        mappedArray[existingYearIndex].teachingPeriods.sort()
                     } else {
                        mappedArray.push({
                           year: offering.year,
                           teachingPeriods: [offering.teachingPeriods]
                        });
                     }
                  }
               });

               return mappedArray;
            }, []);


            // create default offerings from list of all offerings that's still valid (i.e. no end year)
            const defaultOfferings = locationSpecificOfferings
               .filter(offering => !offering.endYear)
               .map(offering => {
                  return this.mapStandardTeachingPeriods(teachingPeriods, defaultTeachingScheduled, Math.max(offering.startYear, startYear), offering.teachingScheduled)
               })
               //filter null teaching periods (no mapping available)
               .filter(offering => offering)
               .filter(onlyUnique);

            if (defaultOfferings.length) {
               u.defaultOfferings = [...new Set(defaultOfferings)];
            } else {
               u.defaultOfferings = []
            }
         } else {
            u.defaultOfferings = [];
         }

         if (item.relationship.unit) {
            u.prerequisites = this.convertRequisites(item.relationship.unit.requisites, "Prerequisite")

            u.anticorequisites = this.convertRequisites(item.relationship.unit.requisites, "Anti Co-requisite")

            u.corequisites = this.convertCoRequisites(item.relationship.unit.requisites)
         } else {
            console.log('Missing Unit Relationship Data', item.relationship?.code, item)
         }

         return u;
   }


   /**
    * Helper functions that extracts and converts unit requisites into a simplified pre-requisites object used by course-plan-builder
    * @param {Array} requisites unit requisites
    * @returns the prerequisites
    */
    static convertCoRequisites(requisites) {
      //if there are no requsites, return an empty object.
      if (!requisites)
         return {};

      var reqs = requisites.filter(requisites => requisites.type.toLowerCase() === "co-requisite");
      //flatten the co-req list to it's simplest form.
      if (reqs.length >= 1 && reqs[0].containers.length >= 1)
      {
         let reqType = {};
         reqType[reqs[0].containers[0].connector.toLowerCase()] = [ ...this.processCoReqContainers(reqs[0].containers[0])];
         return reqType
      }
      return null;
   }

   static processCoReqContainers(container) {
      var units = [];
      for (var j = 0; j < container.relationships.length; j++) {
         if (container.relationships[j].code)
            units.push(container.relationships[j].code)
      }
      return units
   }



   /**
    * Helper functions that extracts and converts unit requisites into a simplified pre-requisites object used by course-plan-builder
    * @param {Array} requisites unit requisites
    * @returns the prerequisites
    */
   static convertRequisites(requisites, type) {
      //if there are no requsites, return an empty object.
      if (!requisites)
         return {}

      let reqs = requisites.filter(requisites => requisites.type.toLowerCase() === type.toLowerCase())
      if (!reqs.length) return null

      //any container within the prerequsite array is considered an Implicit "and" this follows the logic provided by courseloop.
      let reqType = {}
      let con = 'and'
      reqType[con] = []
      if (reqs[0].containers.length)
      {
         reqType[con] = reqs[0].containers.map(c => this.processReqContainer(c))
         return reqType
      }
      return null
   }

   static processReqContainer(container) {
      var units = {};
      units[container.connector.toLowerCase()] = [];
      for (var i = 0; i < container.containers.length; i++) {
         units[container.connector.toLowerCase()].push(this.processReqContainer(container.containers[i]))
      }
      for (var j = 0; j < container.relationships.length; j++) {
         if (container.relationships[j].code)
            units[container.connector.toLowerCase()].push(container.relationships[j].code)
      }
      return units
   }

   /**
    * A helper function to find the standard teaching period for a given teaching scheduled in year
    * @param {Object} teachingPeriods a key value pair of year and teaching periods
    * @param {Object} defaultTeachingScheduled an array of valid teaching periods for any year.
    * @param {Number} year the year for this teaching scheduled
    * @param {String} teachingScheduled the teaching scheduled
    */
   static mapStandardTeachingPeriods(teachingPeriods, defaultTeachingScheduled, year, teachingScheduled) {
      let standardTeachingPeriod = null;
      //check to see if this is already standard, then we need no mapping.
      if (defaultTeachingScheduled) {
         // console.log(`Mapping default teaching scheduled: ${teachingScheduled}`, defaultTeachingScheduled)
         const matchedDefaultTeachingPeriod = defaultTeachingScheduled.find(tp => teachingScheduled.startsWith(tp+':'))
         if (matchedDefaultTeachingPeriod) {
            standardTeachingPeriod = matchedDefaultTeachingPeriod;
            // console.log('Teaching scheduled: ' + teachingScheduled + ' Already Standard Period: ' + standardTeachingPeriod)
         }
      }
      //if we still cant find the mapping, try the teaching periods for this year.
      if (standardTeachingPeriod == null && teachingPeriods[year]) {
         // console.log(`Year ${year} Teaching Periods`, teachingPeriods[year].map(tp => tp.title), 'Trying to map teaching scheduled', teachingScheduled)
         const matchedTeachingPeriod = teachingPeriods[year].find(tp => teachingScheduled.startsWith(tp.title+':'))
         if (matchedTeachingPeriod) {
            standardTeachingPeriod = matchedTeachingPeriod.standardTeachingPeriodMapping;
            // console.log(`Teaching scheduled: ${teachingScheduled} Year ${year} Standard Period: ${standardTeachingPeriod}`);
         }
      }

      //if we cant find this year, keep trying until we arrive at THIS year.
      for(let i = year-1; i >= new Date().getFullYear() && standardTeachingPeriod == null; i--)
      {
         // console.log(`Year ${i} Teaching Periods`, teachingPeriods[i]?.map(tp => tp.title), 'Trying to map teaching scheduled', teachingScheduled)
         if (standardTeachingPeriod == null && teachingPeriods[i]) {
            const matchedTeachingPeriod = teachingPeriods[i].find(tp => teachingScheduled.startsWith(tp.title+':'))
            if (matchedTeachingPeriod) {
               standardTeachingPeriod = matchedTeachingPeriod.standardTeachingPeriodMapping;
               // console.log(`Teaching scheduled: ${teachingScheduled} Year ${i} Standard Period: ${standardTeachingPeriod}`);
            }
         }
      }

      //still no teaching period, we cant map, output a log.
      if (standardTeachingPeriod == null)
         console.log('Unable to map offering: ' + teachingScheduled)

      //now filter by the default teaching period to ensure we have an offering within the selected teaching schedule
      //and return value (or N/A);
      return standardTeachingPeriod != null && defaultTeachingScheduled.some(t=>t == standardTeachingPeriod) ? standardTeachingPeriod : null;
   }

}

module.exports = InputBuilder
